// @ts-nocheck
/**
 * Call Center Users list — redesigned to obd3-gui pattern (FillPage +
 * PageHeader + DataTable + status filter + DropdownMenu row actions +
 * ConfirmDialog). demo-company delete guard preserved.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Pencil, Plus, Settings2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { API_CALL_CENTRE_USERS_LIST, API_USERS_PROFILE } from '@/service/url';
import { getApiCall, deleteApiCallWithHeader } from '@/service/httpCall';
import { getUser } from '@/utilities/setting';
import { ROUTES } from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';

const CallCentreUsersList = () => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [userlist, setUserlist] = useState<any[]>([]);
  const [callCentreName, setCallCentreName] = useState('');
  const [callCentreId, setCallCentreId] = useState<any>(undefined);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const statusOptions = ['Active', 'Inactive'];

  useEffect(() => {
    const resp = getUser();
    if (resp) {
      setCallCentreName(resp.username);
      setCallCentreId(resp.id);
    }
  }, []);

  useEffect(() => {
    if (callCentreId) {
      setLoading(true);
      const url = API_CALL_CENTRE_USERS_LIST.replace(':id', callCentreId);
      getApiCall(url, {})
        .then((resp: any) => {
          const updated = (resp || []).map((val: any, idx: number) => ({
            ...val,
            key: idx + 1,
            created_at: new Date(val['created_at']),
          }));
          setUserlist(updated);
        })
        .catch((err: any) => {
          if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
            errorCheck(err, navigate, setToastProps, setShowToast);
          } else {
            console.log(err);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [callCentreId]);

  const requestDelete = (rowData: any) => {
    try {
      const u = getUser();
      const c = u?.assignedCompany || u?.assigned_company_details || u?.company || null;
      if (c?.is_demo_company === true) {
        toast.warning('Users of a demo company cannot be deleted.');
        return;
      }
    } catch {
      /* ignore */
    }
    checkAuth(setToastProps, setShowToast);
    setPendingDelete(rowData);
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const url = API_USERS_PROFILE.replace(':id', pendingDelete.id);
    deleteApiCallWithHeader(url, {})
      .then(() => {
        toast.success('User deleted successfully');
        setUserlist((prev) => prev.filter((val) => val.id != pendingDelete.id));
        setPendingDelete(null);
      })
      .catch((err: any) => {
        console.log(err);
        toast.warning(`Unable to delete user: ${err}`);
      })
      .finally(() => setDeleting(false));
  };

  const displayedUserList = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    return userlist.filter((u: any) => {
      if (statusFilter) {
        const derived = Number(u.status) === 1 ? 'Active' : 'Inactive';
        if (derived !== statusFilter && u.status !== statusFilter) return false;
      }
      if (q) {
        const hay = [u.username, u.name, u.email]
          .map((v) => (v == null ? '' : String(v)))
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [userlist, searchValue, statusFilter]);

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'key',
        header: '#',
        size: 60,
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{row.original.key}</span>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Username',
        cell: ({ row }) => (
          <span className="font-medium text-foreground">{row.original.username}</span>
        ),
      },
      { accessorKey: 'name', header: 'Name' },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.email || '—'}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        cell: ({ row }) => {
          const active = Number(row.original.status) === 1;
          return (
            <span
              className={
                active
                  ? 'inline-flex rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400'
                  : 'inline-flex rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'
              }
            >
              {active ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },
      {
        id: 'created_at',
        header: 'Joined',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {new Date(row.original.created_at).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const editUserLink = ROUTES.CALL_CENTRES.USERS.EDIT.replace(
            ':id',
            callCentreId,
          ).replace(':uid', rowData.id);
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Row actions"
                >
                  <Settings2 className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuLabel className="truncate">
                  {rowData.username || rowData.name}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(`/${editUserLink}`)}>
                  <Pencil className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => requestDelete(rowData)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callCentreId, userlist],
  );

  if (loading) return <Loader />;

  return (
    <FillPage>
      <PageHeader
        title={callCentreName ? `${callCentreName} — Users` : 'Users'}
        description="Users provisioned inside this call center tenant."
        actions={
          <Button onClick={() => navigate(`/${ROUTES.USERS.CREATE}`)}>
            <Plus className="h-4 w-4" />
            Create User
          </Button>
        }
      />

      <div className="mb-4 rounded-md border border-border bg-card p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_180px] md:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="search" className="text-xs">
              Search
            </Label>
            <Input
              id="search"
              type="text"
              placeholder="Search by username / name / email…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="status" className="text-xs">
              Status
            </Label>
            <Select
              value={statusFilter || 'all'}
              onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger id="status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {statusOptions.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <DataTable
        tableId="callcentres.userlist:v1"
        columns={columns}
        data={displayedUserList}
        isLoading={loading}
        emptyTitle="No users"
        emptyDescription="No users match the current filters."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={30}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this user?"
        description={
          <>
            User{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.username || pendingDelete?.name || `#${pendingDelete?.id}`}
            </span>{' '}
            will be permanently deleted.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
};

export default CallCentreUsersList;
